import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash-es/isEqual';
import { ReactComponent as DeleteIcon } from 'assets/iconButtons/delete-new-icon.svg';
import i18nContext from 'components/i18n-context';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { Toggle } from 'uikit/Toggle/Toggle';
import {
  getEmptyValue,
  getEnumLabel,
  getFieldLabel,
  getSchemaType,
  isEnumArraySchema,
  isMapSchema,
  isSupportedSchema,
  joinPath,
  SCHEMA_TYPE
} from './schemaUtils';

const INTEGER_PATTERN = /^-?\d+$/;
const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;

// Per-form settings: input names (and ids) are prefixed so two forms on one page never share an id,
// and a read-only form hides the controls that add or remove items
export const SchemaFormContext = createContext({ idPrefix: 'schema-form', isReadOnly: false });

const useInputName = () => {
  const { idPrefix } = useContext(SchemaFormContext);
  return (path) => `${idPrefix}-${path || 'root'}`;
};

const getEnumOptions = (values) => values.map((value) => ({ key: value, value: getEnumLabel(value) }));

const setObjectProperty = (object, key, value) => {
  const result = { ...(object || {}) };
  if (value === undefined) {
    delete result[key];
  } else {
    result[key] = value;
  }
  return result;
};

const toJsonText = (value) => (value === undefined ? '' : JSON.stringify(value, null, 2));

const FieldError = ({ message }) => (message ? <p className={'schema-form-error'}>{message}</p> : null);

FieldError.propTypes = {
  message: PropTypes.string
};

const GroupHeader = ({ label, isRequired, description, error }) => (
  <>
    {label && (
      <h4 className={'schema-form-group-title'}>
        {label}
        {isRequired && '*'}
      </h4>
    )}
    {description && <p className={'schema-form-hint'}>{description}</p>}
    <FieldError message={error} />
  </>
);

GroupHeader.propTypes = {
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  description: PropTypes.string,
  error: PropTypes.string
};

const RemoveButton = ({ onClick, isDisabled }) => {
  const i18n = useContext(i18nContext);
  const { isReadOnly } = useContext(SchemaFormContext);

  if (isReadOnly) return null;

  return (
    <button
      type={'button'}
      className={'schema-form-remove-button'}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={i18n.getMessage('integrationSettings.form.button.remove')}
      title={i18n.getMessage('integrationSettings.form.button.remove')}
    >
      <DeleteIcon />
    </button>
  );
};

const AddButton = ({ onClick, isDisabled }) => {
  const i18n = useContext(i18nContext);
  const { isReadOnly } = useContext(SchemaFormContext);

  if (isReadOnly) return null;

  return (
    <Button
      className={'schema-form-add-button'}
      type={'outline'}
      size={'small'}
      onClick={onClick}
      isDisabled={isDisabled}
    >
      {i18n.getMessage('integrationSettings.form.button.add')}
    </Button>
  );
};

RemoveButton.propTypes = {
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool
};

AddButton.propTypes = {
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool
};

const TextField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();

  return (
    <Input
      className={'schema-form-input'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      value={value ?? ''}
      onChange={({ target }) => onChange(target.value)}
      error={errors[path]}
      subText={schema.description}
      max={schema.maxLength}
      isDisabled={isDisabled}
    />
  );
};

// Keeps an incomplete number ("1.", "-") as text so the operator can finish typing; validation flags it meanwhile
const NumberField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();
  const pattern = getSchemaType(schema) === SCHEMA_TYPE.INTEGER ? INTEGER_PATTERN : NUMBER_PATTERN;

  const handleChange = ({ target }) => {
    const text = target.value.trim();
    if (!text) {
      onChange(undefined);
    } else {
      onChange(pattern.test(text) ? Number(text) : text);
    }
  };

  return (
    <Input
      className={'schema-form-input schema-form-input-number'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      value={value === undefined || value === null ? '' : String(value)}
      onChange={handleChange}
      error={errors[path]}
      subText={schema.description}
      isDisabled={isDisabled}
    />
  );
};

const BooleanField = ({ schema, value, path, label, errors, onChange, isDisabled }) => (
  <div className={'schema-form-boolean'}>
    <Toggle isEnabled={Boolean(value)} onChange={onChange} disabled={isDisabled} label={label} />
    {schema.description && <p className={'schema-form-hint'}>{schema.description}</p>}
    <FieldError message={errors[path]} />
  </div>
);

const EnumField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();

  return (
    <InputDropDown
      className={'schema-form-input'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      options={getEnumOptions(schema.enum)}
      value={value}
      onChange={(name, selectedValue) => onChange(selectedValue)}
      error={errors[path]}
      subText={schema.description}
      isDisabled={isDisabled}
    />
  );
};

const EnumArrayField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();

  return (
    <InputDropDown
      className={'schema-form-input'}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      options={getEnumOptions(schema.items.enum)}
      value={Array.isArray(value) ? value : []}
      onChange={(name, selectedValues) => onChange(selectedValues)}
      error={errors[path]}
      subText={schema.description}
      isMulti
      isDisabled={isDisabled}
    />
  );
};

const ObjectField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled, isInline }) => {
  const required = schema.required || [];

  return (
    <div className={`schema-form-object ${isInline ? 'schema-form-object-inline' : ''}`}>
      {isInline ? (
        <FieldError message={errors[path]} />
      ) : (
        <GroupHeader label={label} isRequired={isRequired} description={schema.description} error={errors[path]} />
      )}
      <div className={'schema-form-object-fields'}>
        {Object.entries(schema.properties || {}).map(([key, propertySchema]) => (
          <SchemaField
            key={key}
            schema={propertySchema}
            value={value?.[key]}
            path={joinPath(path, key)}
            label={getFieldLabel(propertySchema, key)}
            isRequired={required.includes(key)}
            errors={errors}
            onChange={(propertyValue) => onChange(setObjectProperty(value, key, propertyValue))}
            isDisabled={isDisabled}
          />
        ))}
      </div>
    </div>
  );
};

const ArrayField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const i18n = useContext(i18nContext);
  const items = Array.isArray(value) ? value : [];
  const isMaxItemsReached = schema.maxItems !== undefined && items.length >= schema.maxItems;

  const updateItem = (index, itemValue) => onChange(items.map((item, i) => (i === index ? itemValue : item)));
  const removeItem = (index) => onChange(items.filter((item, i) => i !== index));
  const addItem = () => onChange([...items, getEmptyValue(schema.items)]);

  return (
    <div className={'schema-form-array'}>
      <GroupHeader label={label} isRequired={isRequired} description={schema.description} error={errors[path]} />
      {items.length === 0 && (
        <p className={'schema-form-empty'}>{i18n.getMessage('integrationSettings.form.noItems')}</p>
      )}
      {items.map((item, index) => (
        <div className={'schema-form-row'} key={index}>
          <div className={'schema-form-row-content'}>
            <SchemaField
              schema={schema.items}
              value={item}
              path={joinPath(path, index)}
              errors={errors}
              onChange={(itemValue) => updateItem(index, itemValue)}
              isDisabled={isDisabled}
              isInline
            />
          </div>
          <RemoveButton onClick={() => removeItem(index)} isDisabled={isDisabled} />
        </div>
      ))}
      <AddButton onClick={addItem} isDisabled={isDisabled || isMaxItemsReached} />
    </div>
  );
};

const MapField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();
  const i18n = useContext(i18nContext);
  const entries = Object.entries(value || {});
  const usedKeys = entries.map(([key]) => key);
  const allowedKeys = schema.propertyNames?.enum;
  const nextKey = allowedKeys ? allowedKeys.find((key) => !usedKeys.includes(key)) : '';
  const isAddDisabled = isDisabled || nextKey === undefined || usedKeys.includes(nextKey);

  const renameKey = (oldKey, newKey) => {
    // Two entries cannot share a key; the rename waits until the operator types a free one
    if (newKey === oldKey || usedKeys.includes(newKey)) return;
    onChange(Object.fromEntries(entries.map(([key, entryValue]) => [key === oldKey ? newKey : key, entryValue])));
  };
  const updateEntry = (key, entryValue) => onChange(setObjectProperty(value, key, entryValue));
  const removeEntry = (key) => onChange(Object.fromEntries(entries.filter(([entryKey]) => entryKey !== key)));
  const addEntry = () => onChange({ ...(value || {}), [nextKey]: getEmptyValue(schema.additionalProperties) });

  return (
    <div className={'schema-form-array'}>
      <GroupHeader label={label} isRequired={isRequired} description={schema.description} error={errors[path]} />
      {entries.length === 0 && (
        <p className={'schema-form-empty'}>{i18n.getMessage('integrationSettings.form.noItems')}</p>
      )}
      {entries.map(([key, entryValue], index) => (
        <div className={'schema-form-row'} key={index}>
          <div className={'schema-form-row-content schema-form-object-inline'}>
            <div className={'schema-form-object-fields'}>
              {allowedKeys ? (
                <InputDropDown
                  className={'schema-form-input'}
                  name={getInputName(joinPath(path, `key${index}`))}
                  label={i18n.getMessage('integrationSettings.form.mapKey')}
                  isRequired
                  options={getEnumOptions(
                    allowedKeys.filter((allowedKey) => !usedKeys.includes(allowedKey) || allowedKey === key)
                  )}
                  value={key}
                  onChange={(name, newKey) => renameKey(key, newKey)}
                  isDisabled={isDisabled}
                />
              ) : (
                <Input
                  className={'schema-form-input'}
                  name={getInputName(joinPath(path, `key${index}`))}
                  label={i18n.getMessage('integrationSettings.form.mapKey')}
                  isRequired
                  value={key}
                  onChange={({ target }) => renameKey(key, target.value)}
                  isDisabled={isDisabled}
                />
              )}
              <SchemaField
                schema={schema.additionalProperties}
                value={entryValue}
                path={joinPath(path, key)}
                label={i18n.getMessage('integrationSettings.form.mapValue')}
                isRequired
                errors={errors}
                onChange={(newEntryValue) => updateEntry(key, newEntryValue)}
                isDisabled={isDisabled}
                isInline
              />
            </div>
          </div>
          <RemoveButton onClick={() => removeEntry(key)} isDisabled={isDisabled} />
        </div>
      ))}
      <AddButton onClick={addEntry} isDisabled={isAddDisabled} />
    </div>
  );
};

// Fallback for schema constructs the form does not render: the value is edited as raw JSON
const JsonField = ({ schema, value, path, label, isRequired, errors, onChange, isDisabled }) => {
  const getInputName = useInputName();
  const i18n = useContext(i18nContext);
  const [text, setText] = useState(() => toJsonText(value));
  const [parseError, setParseError] = useState(null);

  useEffect(() => {
    // Replace the text only when the value changed from outside (discard, reload), not while the operator types
    setText((currentText) => {
      try {
        if (isEqual(JSON.parse(currentText), value)) return currentText;
      } catch (e) {
        // the current text is not valid JSON, so it does not represent the new value
      }
      return toJsonText(value);
    });
    setParseError(null);
  }, [value]);

  const handleChange = ({ target }) => {
    setText(target.value);
    if (!target.value.trim()) {
      setParseError(null);
      onChange(undefined);
      return;
    }
    try {
      const parsedValue = JSON.parse(target.value);
      setParseError(null);
      onChange(parsedValue);
    } catch (e) {
      setParseError(i18n.getMessage('integrationSettings.form.error.invalidJson'));
    }
  };

  return (
    <Input
      className={'schema-form-input schema-form-json'}
      type={'textarea'}
      rows={8}
      name={getInputName(path)}
      label={label}
      isRequired={isRequired}
      value={text}
      onChange={handleChange}
      error={parseError || errors[path]}
      subText={schema?.description || i18n.getMessage('integrationSettings.form.jsonHint')}
      isDisabled={isDisabled}
    />
  );
};

const SchemaField = (props) => {
  const { schema } = props;

  if (!isSupportedSchema(schema)) {
    return <JsonField {...props} />;
  }

  const type = getSchemaType(schema);
  if (type === SCHEMA_TYPE.OBJECT) {
    return isMapSchema(schema) ? <MapField {...props} /> : <ObjectField {...props} />;
  }
  if (type === SCHEMA_TYPE.ARRAY) {
    return isEnumArraySchema(schema) ? <EnumArrayField {...props} /> : <ArrayField {...props} />;
  }
  if (type === SCHEMA_TYPE.BOOLEAN) {
    return <BooleanField {...props} />;
  }
  if (type === SCHEMA_TYPE.INTEGER || type === SCHEMA_TYPE.NUMBER) {
    return <NumberField {...props} />;
  }
  return schema.enum ? <EnumField {...props} /> : <TextField {...props} />;
};

const fieldPropTypes = {
  schema: PropTypes.object,
  value: PropTypes.any,
  path: PropTypes.string,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  isInline: PropTypes.bool
};

TextField.propTypes = fieldPropTypes;
NumberField.propTypes = fieldPropTypes;
BooleanField.propTypes = fieldPropTypes;
EnumField.propTypes = fieldPropTypes;
EnumArrayField.propTypes = fieldPropTypes;
ObjectField.propTypes = fieldPropTypes;
ArrayField.propTypes = fieldPropTypes;
MapField.propTypes = fieldPropTypes;
JsonField.propTypes = fieldPropTypes;
SchemaField.propTypes = fieldPropTypes;

export default SchemaField;
